import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Printer, X, Share2 } from "lucide-react";
import { toast } from "sonner";
import KidButton from "@/components/KidButton";
import { loadProgress, awardBadge } from "@/lib/progress";
import { trackEvent } from "@/lib/analytics";
import { printCertificate } from "@/lib/printCertificate";
import { tryCertificateFile } from "@/lib/certificateToImage";

/**
 * <MilestoneCertificate /> — auto-shows when the kid crosses any of the
 * supported milestones. Prints / shares with their NAME on it for max parent
 * reach (refer-a-friend) and child pride.
 *
 * Triggers (first time each):
 *   - 10 countries learned   → "Mini Explorer"
 *   - 25 countries learned   → "World Tourist"
 *   - 7-day streak           → "Week Warrior"
 *   - 30-day streak          → "Volcano Streak"
 *   - 100 stars              → "Star Burst"
 *   - All 6 Knowledge worlds → "Universe of Wonder"
 *
 * Persistence: each cert id is stored in `we_certs_seen` so we never show
 * the same one twice. Kid's name is in `we_kid_name`; if missing we ask
 * via an inline input before unveiling the certificate.
 */

const NAME_KEY = "we_kid_name";
const SEEN_KEY = "we_certs_seen_v1";

const MILESTONES = [
  { id: "mini-explorer",   title: "Mini Explorer",  emoji: "🌍", line: "Learned 10 countries", check: (p) => (p.countriesLearned || []).length >= 10 },
  { id: "world-tourist",   title: "World Tourist",  emoji: "🗺️", line: "Learned 25 countries", check: (p) => (p.countriesLearned || []).length >= 25 },
  { id: "week-warrior",    title: "Week Warrior",   emoji: "🔥", line: "Earned a 7-day streak", check: (p) => (p.streak || 0) >= 7 },
  { id: "volcano-streak",  title: "Volcano Streak", emoji: "🌋", line: "Kept a 30-day streak", check: (p) => (p.streak || 0) >= 30 },
  { id: "star-burst",      title: "Star Burst",     emoji: "🌟", line: "Earned 100 stars",     check: (p) => ((p.totalStars || p.stars || 0) >= 100) },
  { id: "universe-wonder", title: "Universe of Wonder", emoji: "📚", line: "Tried all 6 Knowledge worlds", check: (p) => {
      const ev = p.activity || [];
      const worlds = new Set(ev.filter((a) => typeof a.type === "string" && a.type.startsWith("knowledge:")).map((a) => a.type.split(":")[1]));
      return worlds.size >= 6;
    } },
];

function loadSeen() {
  try { return JSON.parse(localStorage.getItem(SEEN_KEY) || "[]"); }
  catch { return []; }
}
function saveSeen(arr) {
  try { localStorage.setItem(SEEN_KEY, JSON.stringify(arr)); } catch {/* ignore */}
}

export default function MilestoneCertificate() {
  const [active, setActive] = useState(null);   // milestone object
  const [name, setName] = useState(() => { try { return localStorage.getItem(NAME_KEY) || ""; } catch { return ""; } });
  const [editingName, setEditingName] = useState(false);
  const certRef = useRef(null);

  useEffect(() => {
    const check = () => {
      if (active) return;
      const p = loadProgress();
      const seen = new Set(loadSeen());
      for (const m of MILESTONES) {
        if (!seen.has(m.id) && m.check(p)) {
          setActive(m);
          seen.add(m.id);
          saveSeen([...seen]);
          awardBadge(`cert-${m.id}`, m.title);
          trackEvent("milestone_cert_unlocked", { id: m.id });
          return;
        }
      }
    };
    check();
    const onChange = () => check();
    window.addEventListener("we-progress-changed", onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener("we-progress-changed", onChange);
      window.removeEventListener("storage", onChange);
    };
  }, [active]);

  if (!active) return null;

  const saveName = (v) => {
    const trimmed = (v || "").trim().slice(0, 24);
    setName(trimmed);
    try { localStorage.setItem(NAME_KEY, trimmed); } catch {/* ignore */}
    setEditingName(false);
  };

  const print = () => {
    trackEvent("milestone_cert_print", { id: active.id });
    // Isolated print — never drags the rest of the app into the PDF.
    // The QR corner links back to the site.
    printCertificate({
      innerHtml: certRef.current ? certRef.current.outerHTML : "",
      title: `${active.title} — My World Quest`,
      url: "https://myworld.quest",
      footerText: "Scan to visit\nMy World Quest",
    });
  };
  const share = async () => {
    const text = `${name || "I"} just earned the "${active.title}" certificate on My World Quest! 🌍  https://myworld.quest`;
    trackEvent("milestone_cert_share", { id: active.id });
    // Try to attach the certificate as a PNG-ish file first — most
    // WhatsApp/Messages/Email flows preview an image far better than a
    // bare URL. We generate a lightweight PNG from the certificate DOM.
    const files = await tryCertificateFile(certRef, `${active.id}-certificate.png`);
    if (navigator.share) {
      try {
        const payload = { title: "My World Quest Certificate", text, url: "https://myworld.quest" };
        if (files && navigator.canShare && navigator.canShare({ files })) {
          payload.files = files;
        }
        await navigator.share(payload);
        return;
      } catch {/* user cancelled */}
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied! Paste it to share.");
    } catch {
      toast.error("Could not share — try the print button.");
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[120] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 print:bg-white print:p-0 print:static print:backdrop-blur-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          initial={{ scale: 0.85, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.85, y: 20 }}
          transition={{ type: "spring", stiffness: 300, damping: 24 }}
          className="relative w-full max-w-2xl"
        >
          {/* Close — hidden on print */}
          <button
            onClick={() => setActive(null)}
            data-testid="cert-close"
            aria-label="Close certificate"
            className="absolute -top-3 -right-3 z-10 inline-flex items-center justify-center h-10 w-10 rounded-full border-[3px] border-slate-900 bg-white shadow-[0_4px_0_0_rgba(15,23,42,1)] hover:-translate-y-0.5 print:hidden"
          >
            <X size={18} strokeWidth={3} />
          </button>

          <div
            ref={certRef}
            data-testid="milestone-certificate"
            className="rounded-3xl border-[6px] border-slate-900 bg-gradient-to-br from-yellow-100 via-amber-100 to-orange-100 shadow-[10px_10px_0_0_rgba(15,23,42,1)] p-6 md:p-10 text-center relative overflow-hidden print:border-2 print:rounded-none print:shadow-none print:bg-white"
          >
            {/* Confetti glow */}
            <span aria-hidden className="absolute -top-10 -left-10 h-32 w-32 rounded-full bg-pink-300/50 blur-3xl pointer-events-none print:hidden" />
            <span aria-hidden className="absolute -bottom-10 -right-10 h-32 w-32 rounded-full bg-amber-300/50 blur-3xl pointer-events-none print:hidden" />

            <div className="relative">
              <div className="text-xs md:text-sm font-bold uppercase tracking-[0.3em] text-amber-700">
                Certificate of Achievement
              </div>
              <div className="mt-3 text-6xl md:text-7xl" aria-hidden>{active.emoji}</div>
              <h2 className="mt-2 font-heading text-3xl md:text-5xl font-extrabold text-slate-900">
                {active.title}
              </h2>

              <p className="mt-4 text-sm md:text-base font-bold text-slate-700">
                This certificate is proudly awarded to
              </p>

              {/* Kid name — inline editable */}
              {editingName || !name ? (
                <form
                  className="mt-1 flex items-center justify-center gap-2 flex-wrap"
                  onSubmit={(e) => { e.preventDefault(); saveName(e.target.elements.kn.value); }}
                >
                  <input
                    name="kn"
                    autoFocus
                    defaultValue={name}
                    maxLength={24}
                    placeholder="Your name"
                    data-testid="cert-name-input"
                    className="text-2xl md:text-3xl font-heading font-extrabold text-center bg-white border-[3px] border-slate-900 rounded-2xl px-4 py-1.5 w-64 max-w-full shadow-[0_3px_0_0_rgba(15,23,42,1)] focus:outline-none focus:ring-4 focus:ring-amber-500/60"
                  />
                  <KidButton size="sm" variant="yellow" type="submit" data-testid="cert-name-save">Save</KidButton>
                </form>
              ) : (
                <button
                  type="button"
                  onClick={() => setEditingName(true)}
                  data-testid="cert-name"
                  className="mt-1 inline-block text-3xl md:text-4xl font-heading font-extrabold underline decoration-amber-500 decoration-4 underline-offset-4 hover:bg-amber-200 px-2 rounded-xl print:no-underline print:bg-transparent"
                  title="Tap to edit name"
                >
                  {name}
                </button>
              )}

              <p className="mt-4 text-sm md:text-base font-bold text-slate-700">
                for the achievement of
              </p>
              <p className="mt-1 font-heading text-xl md:text-2xl font-extrabold text-slate-900">
                {active.line}
              </p>

              <div className="mt-6 flex items-center justify-center gap-4 md:gap-6 text-xs font-bold text-slate-600">
                <div className="flex flex-col items-center">
                  <div className="border-b-2 border-slate-400 w-32 md:w-40 mb-1" />
                  <span>{new Date().toLocaleDateString()}</span>
                  <span className="opacity-60">Date</span>
                </div>
                <div className="flex flex-col items-center">
                  <div className="text-3xl">🌍</div>
                  <span>My World Quest</span>
                  <span className="opacity-60">myworld.quest</span>
                </div>
              </div>
            </div>
          </div>

          {/* Actions — hidden on print */}
          <div className="mt-4 flex flex-wrap items-center justify-center gap-3 print:hidden">
            <KidButton variant="yellow" size="md" onClick={print} data-testid="cert-print">
              <Printer size={16} strokeWidth={3} /> Print / Save PDF
            </KidButton>
            <KidButton variant="pink" size="md" onClick={share} data-testid="cert-share">
              <Share2 size={16} strokeWidth={3} /> Share
            </KidButton>
            <button
              onClick={() => setActive(null)}
              className="inline-flex items-center gap-1 px-4 py-2.5 rounded-full border-[3px] border-slate-900 bg-white font-bold text-sm shadow-[0_4px_0_0_rgba(15,23,42,1)] hover:-translate-y-0.5 transition-all"
              data-testid="cert-dismiss"
            >
              Keep exploring
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
